/**
 * audio.ts â€” WEB.4.P2.1 / WEB.4.BGM.1 â€” Howler wrapper.
 *
 * Single audio service with two channels:
 *   - BGM: looped background music, cross-faded on scene/era change
 *   - SFX: short one-shots fired by user actions (dice-roll, card-draw, etc.)
 *
 * BGM API:
 *   playBGM(key)                  â€” start looping a track (cross-fades from current)
 *   crossfadeBGM(key, durationMs)  â€” same with explicit fade duration
 *   stopBGM(fadeMs?)               â€” fade out + stop
 *
 * BGM key â†’ /audio/bgm/<filename>.mp3
 * MP3 served; Howler html5 mode streams large files.
 * Source WAVs kept on disk; only MP3 shipped in dist.
 *
 * Era variant selection:
 *   Each era lasts 3 rounds. Round 1-2 within era â†’ A variant; round 3 â†’ B.
 *   Helper: bgmKeyForEraRound(era, round).
 *
 * State (mute, volume) lives in the Zustand store so the user's preference
 * persists across reloads via localStorage.
 */

import { Howl, Howler } from "howler";

// ---------------------------------------------------------------------------
// BGM key registry
// ---------------------------------------------------------------------------

/** All valid BGM track keys.
 *
 * R7 #4/#10 file inventory (assets/audio/bgm/ after sync):
 *   era-{1-4}-{a|b}.wav          â€” era tracks (all present)
 *   terrain-plains-1/2.wav       â€” Plains (two variants, use -1)
 *   terrain-forest-1/2.wav       â€” Forest (two variants, use -1)
 *   terrain-mountain.wav         â€” Mountain
 *   terrain-desert.wav           â€” Desert
 *   terrain-tundra-1.wav         â€” Tundra (only variant)
 *   terrain-river.wav            â€” River
 *   terrain-coastline.wav        â€” Coast
 *   terrain-urban.wav            â€” Urban
 *   terrain-volcanic-1/2.wav     â€” Volcanic (use -1)
 *   terrain-jail.wav             â€” Jail
 *   terrain-trading.wav          â€” Trading
 *   terrain-teleport.wav         â€” Teleport
 *   NO terrain-event/policy/start/menu/victory/defeat/intro.mp3
 *     â†’ fallbacks: Eventâ†’era-1-a, Policyâ†’era-2-a, Startâ†’era-1-a
 *       (era BGM continues â€” no jarring silence on special tiles).
 */
export type BGMKey =
  | "menu"
  | "menu-1"
  | "menu-2"
  | "char-select-1"
  | "char-select-2"
  | "era-1-a"
  | "era-1-b"
  | "era-2-a"
  | "era-2-b"
  | "era-3-a"
  | "era-3-b"
  | "era-4-a"
  | "era-4-b"
  | "terrain-plains-1"
  | "terrain-plains-2"
  | "terrain-forest-1"
  | "terrain-forest-2"
  | "terrain-mountain"
  | "terrain-desert"
  | "terrain-tundra-1"
  | "terrain-river"
  | "terrain-coastline"
  | "terrain-urban"
  | "terrain-volcanic-1"
  | "terrain-volcanic-2"
  | "terrain-jail"
  | "terrain-trading"
  | "terrain-teleport"
  // R10.5: dedicated pool tracks for Event/Policy/Start tilestage.
  // bgmKeyForTerrain picks one at random per landing (user spec: "any of pool").
  | "event-1"
  | "event-2"
  | "event-3"
  | "policy-1"
  | "policy-2"
  | "start"
  // R10.5: ending + cutscene tracks (replace prior victory/defeat/intro routing).
  | "ending-a"
  | "ending-b"
  | "ending-c"
  | "ending-d"
  | "cutscene-intro"
  | "cutscene-transition-1"
  | "cutscene-transition-2"
  | "victory"
  | "defeat"
  | "intro";

const BGM_URLS: Readonly<Record<BGMKey, string>> = {
  // Legacy `menu` aliases to menu-1 (first variant) for back-compat.
  menu: "/audio/bgm/menu-1.mp3",
  "menu-1": "/audio/bgm/menu-1.mp3",
  "menu-2": "/audio/bgm/menu-2.mp3",
  "char-select-1": "/audio/bgm/char-select-1.mp3",
  "char-select-2": "/audio/bgm/char-select-2.mp3",
  "era-1-a": "/audio/bgm/era-1-a.mp3",
  "era-1-b": "/audio/bgm/era-1-b.mp3",
  "era-2-a": "/audio/bgm/era-2-a.mp3",
  "era-2-b": "/audio/bgm/era-2-b.mp3",
  "era-3-a": "/audio/bgm/era-3-a.mp3",
  "era-3-b": "/audio/bgm/era-3-b.mp3",
  "era-4-a": "/audio/bgm/era-4-a.mp3",
  "era-4-b": "/audio/bgm/era-4-b.mp3",
  "terrain-plains-1": "/audio/bgm/terrain-plains-1.mp3",
  "terrain-plains-2": "/audio/bgm/terrain-plains-2.mp3",
  "terrain-forest-1": "/audio/bgm/terrain-forest-1.mp3",
  "terrain-forest-2": "/audio/bgm/terrain-forest-2.mp3",
  "terrain-mountain": "/audio/bgm/terrain-mountain.mp3",
  "terrain-desert": "/audio/bgm/terrain-desert.mp3",
  "terrain-tundra-1": "/audio/bgm/terrain-tundra-1.mp3",
  "terrain-river": "/audio/bgm/terrain-river.mp3",
  "terrain-coastline": "/audio/bgm/terrain-coastline.mp3",
  "terrain-urban": "/audio/bgm/terrain-urban.mp3",
  "terrain-volcanic-1": "/audio/bgm/terrain-volcanic-1.mp3",
  "terrain-volcanic-2": "/audio/bgm/terrain-volcanic-2.mp3",
  "terrain-jail": "/audio/bgm/terrain-jail.mp3",
  "terrain-trading": "/audio/bgm/terrain-trading.mp3",
  "terrain-teleport": "/audio/bgm/terrain-teleport.mp3",
  "event-1": "/audio/bgm/event-1.mp3",
  "event-2": "/audio/bgm/event-2.mp3",
  "event-3": "/audio/bgm/event-3.mp3",
  "policy-1": "/audio/bgm/policy-1.mp3",
  "policy-2": "/audio/bgm/policy-2.mp3",
  start: "/audio/bgm/start.mp3",
  "ending-a": "/audio/bgm/ending-a.mp3",
  "ending-b": "/audio/bgm/ending-b.mp3",
  "ending-c": "/audio/bgm/ending-c.mp3",
  "ending-d": "/audio/bgm/ending-d.mp3",
  "cutscene-intro": "/audio/bgm/cutscene-intro.mp3",
  "cutscene-transition-1": "/audio/bgm/cutscene-transition-1.mp3",
  "cutscene-transition-2": "/audio/bgm/cutscene-transition-2.mp3",
  // Legacy keys â€” alias to closest new track for back-compat.
  victory: "/audio/bgm/ending-a.mp3",
  defeat: "/audio/bgm/ending-c.mp3",
  intro: "/audio/bgm/cutscene-intro.mp3",
};

// ---------------------------------------------------------------------------
// SFX registry (unchanged from WEB.4.P2.1)
// ---------------------------------------------------------------------------

/** Map from a stable SFX id to a relative URL (under /audio/sfx/). */
const SFX_URLS: Readonly<Record<string, string>> = {
  "dice-roll": "/audio/sfx/dice-roll.mp3",
  "card-draw": "/audio/sfx/card-draw.mp3",
  "card-play": "/audio/sfx/card-play.mp3",
  build: "/audio/sfx/build.mp3",
  "toll-paid": "/audio/sfx/toll-paid.mp3",
  jail: "/audio/sfx/jail.mp3",
  bankruptcy: "/audio/sfx/bankruptcy.mp3",
  "era-transition": "/audio/sfx/era-transition.mp3",
  "modal-open": "/audio/sfx/modal-open.mp3",
  "modal-close": "/audio/sfx/modal-close.mp3",
  "seat-handoff": "/audio/sfx/seat-handoff.mp3",
};

// ---------------------------------------------------------------------------
// Internal state
// ---------------------------------------------------------------------------

/** Cached Howl instances keyed by SFX id. Lazily created. */
const _sfxCache = new Map<string, Howl>();

interface ActiveBGM {
  key: BGMKey;
  howl: Howl;
}
let _currentBgm: ActiveBGM | null = null;

const _loadErrorsLogged = new Set<string>();

/**
 * R12 Item #3 â€” BGM seek cache.
 *
 * When crossfading AWAY from a key, we snapshot its current playback
 * position (seconds) so that when the SAME key is requested again it
 * resumes from where it left off instead of restarting at 0:00.
 *
 * Only era and terrain BGM keys benefit from this (they loop and the
 * user notices the "restart" every time they re-enter the board view).
 * Cutscene / ending tracks are intentionally NOT cached â€” they are
 * one-shot sequences that should always play from the beginning.
 *
 * Cache is cleared by resetAudioForNewGame() and _resetAudioForTesting().
 */
const _bgmSeekCache = new Map<BGMKey, number>();

/** BGM key families that should NOT be resumed mid-track. */
const _SEEK_EXCLUDED_PREFIXES = ["cutscene-", "ending-", "intro", "defeat", "victory"] as const;
function _isSeekExcluded(key: BGMKey): boolean {
  return _SEEK_EXCLUDED_PREFIXES.some((p) => key.startsWith(p));
}

/**
 * R5 #8 â€” Autoplay unlock gate.
 *
 * Modern browsers BLOCK audio until the user has interacted with the page.
 * Howler's html5 Howl silently creates the AudioContext in suspended state and
 * `next.play()` returns a Promise that is rejected, leaving _currentBgm set to
 * the new key but producing no audible sound (and no JS error â€” silent block).
 *
 * Fix: buffer the pending BGM key and defer the actual Howl creation + play
 * until the first user interaction (click / keydown / touchstart).
 *
 * When `_audioContextUnlocked` is true, calls go straight through.
 */
let _audioContextUnlocked = false;
let _pendingBgmKey: BGMKey | null = null;
let _pendingBgmDurationMs = 800;

function _unlockAndFlush(): void {
  if (_audioContextUnlocked) return;
  _audioContextUnlocked = true;
  console.info("[audio] AudioContext unlocked by user interaction.");
  if (_pendingBgmKey !== null) {
    const key = _pendingBgmKey;
    const dur = _pendingBgmDurationMs;
    _pendingBgmKey = null;
    console.info(`[audio] Flushing pending BGM: "${key}"`);
    crossfadeBGM(key, dur);
  }
}

if (typeof window !== "undefined") {
  const UNLOCK_EVENTS = ["click", "keydown", "touchstart"] as const;
  function _onInteraction(): void {
    _unlockAndFlush();
    for (const ev of UNLOCK_EVENTS) {
      window.removeEventListener(ev, _onInteraction, { capture: true });
    }
  }
  for (const ev of UNLOCK_EVENTS) {
    window.addEventListener(ev, _onInteraction, { capture: true, once: true });
  }
}

// ---------------------------------------------------------------------------
// SFX helpers
// ---------------------------------------------------------------------------

function _getSfx(id: string): Howl | null {
  const url = SFX_URLS[id];
  if (url === undefined) return null;
  const cached = _sfxCache.get(id);
  if (cached !== undefined) return cached;
  const howl = new Howl({
    src: [url],
    volume: 1.0,
    preload: false,
    onloaderror: (_soundId: number, err: unknown) => {
      if (!_loadErrorsLogged.has(id)) {
        _loadErrorsLogged.add(id);
        console.info(`[audio] sfx "${id}" missing (${url}) â€” silenced. ${String(err)}`);
      }
    },
  });
  _sfxCache.set(id, howl);
  return howl;
}

/** Play an SFX one-shot. Silent no-op when id unknown / file missing. */
export function playSfx(id: string, volume = 1.0): void {
  const howl = _getSfx(id);
  if (howl === null) return;
  try {
    howl.volume(volume);
    howl.play();
  } catch (err) {
    // Howler can throw if AudioContext isn't unlocked yet (browser autoplay
    // policy). Swallow â€” the SFX is non-essential.
    console.debug(`[audio] playSfx("${id}") threw: ${String(err)}`);
  }
}

// ---------------------------------------------------------------------------
// BGM helpers
// ---------------------------------------------------------------------------

const DEFAULT_CROSSFADE_MS = 800;
const DEFAULT_BGM_VOL = 0.7;

/**
 * Cross-fade to the given BGM key. Same-key = no-op.
 * Silent no-op when the file is missing.
 *
 * R5 #8: if the AudioContext has not yet been unlocked by a user interaction,
 * stores the key as pending and returns â€” the actual Howl play is deferred
 * until `_unlockAndFlush` fires (first click/keydown/touchstart).
 */
export function crossfadeBGM(key: BGMKey, durationMs = DEFAULT_CROSSFADE_MS): void {
  if (_currentBgm !== null && _currentBgm.key === key) {
    // Same key already playing â€” clear any pending override so we don't
    // double-start on unlock.
    if (_pendingBgmKey === key) _pendingBgmKey = null;
    return;
  }

  // Gate: defer until browser allows audio.
  if (!_audioContextUnlocked) {
    console.info(`[audio] crossfadeBGM("${key}") deferred â€” awaiting user interaction.`);
    _pendingBgmKey = key;
    _pendingBgmDurationMs = durationMs;
    return;
  }

  const url = BGM_URLS[key];

  // R12 #3 â€” Save seek position for the outgoing track so we can resume it later.
  if (_currentBgm !== null) {
    const prevKey = _currentBgm.key;
    if (!_isSeekExcluded(prevKey)) {
      const seekSec = _currentBgm.howl.seek() as number;
      if (typeof seekSec === "number" && Number.isFinite(seekSec)) {
        _bgmSeekCache.set(prevKey, seekSec);
        console.info(`[audio] seek saved: "${prevKey}" @ ${seekSec.toFixed(2)}s`);
      }
    }
  }

  // R12 #3 â€” Look up cached seek for the incoming key (era / terrain only).
  const resumeSec = !_isSeekExcluded(key) ? (_bgmSeekCache.get(key) ?? 0) : 0;
  console.info(
    `[audio] crossfadeBGM â†’ "${key}" (${url})${resumeSec > 0 ? ` resume@${resumeSec.toFixed(2)}s` : ""}`,
  );

  const next = new Howl({
    src: [url],
    loop: true,
    volume: 0,
    html5: true,
    onloaderror: (_soundId: number, err: unknown) => {
      const logKey = `bgm:${key}`;
      if (!_loadErrorsLogged.has(logKey)) {
        _loadErrorsLogged.add(logKey);
        console.info(`[audio] bgm "${key}" load error (${url}) â€” silenced. ${String(err)}`);
      }
    },
    onplay: () => {
      // Seek AFTER play() so html5 streaming mode has a valid sound id.
      if (resumeSec > 0) {
        next.seek(resumeSec);
      }
      console.info(
        `[audio] bgm "${key}" playing${resumeSec > 0 ? ` (resumed @ ${resumeSec.toFixed(2)}s)` : ""}.`,
      );
    },
  });
  next.play();
  next.fade(0, DEFAULT_BGM_VOL, durationMs);

  if (_currentBgm !== null) {
    const prev = _currentBgm.howl;
    prev.fade(prev.volume() as number, 0, durationMs);
    // R8 #8: unload() releases Web Audio buffer refs after stop(); stop()-only
    // leaves the Howl in memory holding AudioContext resources indefinitely.
    window.setTimeout(() => {
      prev.stop();
      prev.unload();
    }, durationMs + 50);
  }
  _currentBgm = { key, howl: next };
}

/** Start BGM for a key (alias for crossfadeBGM with default fade). */
export function playBGM(key: BGMKey): void {
  crossfadeBGM(key, DEFAULT_CROSSFADE_MS);
}

/** Stop BGM with a fade-out. */
export function stopBGM(fadeMs = DEFAULT_CROSSFADE_MS): void {
  if (_currentBgm === null) return;
  const prev = _currentBgm.howl;
  prev.fade(prev.volume() as number, 0, fadeMs);
  // R8 #8: unload() after stop() to release Web Audio buffer refs.
  window.setTimeout(() => {
    prev.stop();
    prev.unload();
  }, fadeMs + 50);
  _currentBgm = null;
}

/**
 * Helper for HexBoard: pick era BGM variant based on round-within-era.
 * Cycles every 3 rounds: rounds 1-2 within a 3-cycle â†’ A variant; round 3 â†’ B variant.
 * Era boundaries (CF-1 30-round game): Era1=1-6, Era2=7-13, Era3=14-19, Era4=20-30.
 *
 * roundInEra = ((round - 1) % 3) + 1  (values 1, 2, 3)
 */
export function bgmKeyForEraRound(era: 1 | 2 | 3 | 4, round: number): BGMKey {
  const roundInEra = ((round - 1) % 3) + 1;
  const variant = roundInEra === 3 ? "b" : "a";
  return `era-${era}-${variant}` as BGMKey;
}

/**
 * Resolve tile-type string â†’ terrain BGM key.
 *
 * R7 #4/#10: maps every tile type to an existing WAV key.
 * Tile types without a dedicated track (Event, Policy, Start) fall back to
 * era BGM keys so the era music continues rather than cutting to silence.
 * Returns null only for completely unknown tile types.
 */
export function bgmKeyForTerrain(tileType: string): BGMKey | null {
  // R10.5: random-pool helper. User spec: tilestage BGM "can play any in any
  // era" â€” so terrains with multiple variants and Event/Policy pools draw a
  // fresh random pick per landing.
  function pick<T>(arr: readonly T[]): T {
    return arr[Math.floor(Math.random() * arr.length)] as T;
  }
  const map: Record<string, BGMKey | readonly BGMKey[]> = {
    Plains: ["terrain-plains-1", "terrain-plains-2"] as const,
    Forest: ["terrain-forest-1", "terrain-forest-2"] as const,
    Mountain: "terrain-mountain",
    Desert: "terrain-desert",
    Tundra: "terrain-tundra-1",
    River: "terrain-river",
    Coast: "terrain-coastline",
    Urban: "terrain-urban",
    Volcanic: ["terrain-volcanic-1", "terrain-volcanic-2"] as const,
    Jail: "terrain-jail",
    Trading: "terrain-trading",
    Teleport: "terrain-teleport",
    // R10.5: dedicated pools â€” random pick per landing.
    Event: ["event-1", "event-2", "event-3"] as const,
    Policy: ["policy-1", "policy-2"] as const,
    Start: "start",
  };
  const entry = map[tileType];
  if (entry === undefined) return null;
  return Array.isArray(entry) ? pick(entry) : (entry as BGMKey);
}

/**
 * R10.5 â€” ending BGM key by end-reason. Locked mapping (user 2026-05-18):
 *   renewable-pathway â†’ ending-a (green victory)
 *   economic-hegemony â†’ ending-b (capitalist victory)
 *   tipping-point     â†’ ending-c (climate loss)
 *   mutual-destruction â†’ ending-d (everyone loses)
 *   bankruptcy-solo / round-limit fall back to ending-c (loss vibe).
 */
export function bgmKeyForEndReason(reason: string): BGMKey {
  switch (reason) {
    case "renewable-pathway":
      return "ending-a";
    case "economic-hegemony":
      return "ending-b";
    case "tipping-point":
      return "ending-c";
    case "mutual-destruction":
      return "ending-d";
    default:
      return "ending-c";
  }
}

// ---------------------------------------------------------------------------
// Legacy compat
// ---------------------------------------------------------------------------

/**
 * Legacy helper: switch BGM by era number (era 0 = menu).
 * Prefer crossfadeBGM(bgmKeyForEraRound(era, round)) for precise variant control.
 */
export function playBgmForEra(era: number, _volume = 0.7): void {
  const keyMap: Record<number, BGMKey> = {
    0: "menu",
    1: "era-1-a",
    2: "era-2-a",
    3: "era-3-a",
    4: "era-4-a",
  };
  const key = keyMap[era];
  if (key === undefined) return;
  crossfadeBGM(key);
}

// ---------------------------------------------------------------------------
// Global mute / volume
// ---------------------------------------------------------------------------

/** Set the GLOBAL Howler mute state (affects every channel). */
export function setMuted(muted: boolean): void {
  Howler.mute(muted);
}

/** Set the GLOBAL Howler master volume (0..1). */
export function setMasterVolume(vol: number): void {
  // R14 Codex-C MED: reject NaN / non-finite â€” Howler.volume(NaN) corrupts internal state.
  if (typeof vol !== "number" || !Number.isFinite(vol)) return;
  Howler.volume(Math.max(0, Math.min(1, vol)));
}

// ---------------------------------------------------------------------------
// Mute persistence
// ---------------------------------------------------------------------------

/** Local-storage key for persisting the user's mute preference. */
export const MUTE_STORAGE_KEY = "gdg-audio-muted";

export function loadPersistedMute(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(MUTE_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export function persistMute(muted: boolean): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(MUTE_STORAGE_KEY, muted ? "1" : "0");
  } catch {
    // ignore
  }
}

// ---------------------------------------------------------------------------
// Volume persistence (WEB.4.AR)
// ---------------------------------------------------------------------------

/**
 * Load a persisted 0..1 volume value from localStorage.
 * Returns `defaultVal` when not set or unparseable.
 */
export function loadPersistedVolume(key: string, defaultVal: number): number {
  if (typeof window === "undefined") return defaultVal;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return defaultVal;
    const v = Number.parseFloat(raw);
    if (!Number.isFinite(v) || v < 0 || v > 1) return defaultVal;
    return v;
  } catch {
    return defaultVal;
  }
}

/** Persist a 0..1 volume value to localStorage (best-effort). */
export function persistVolume(key: string, value: number): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    // ignore
  }
}

// ---------------------------------------------------------------------------
// New Game reset
// ---------------------------------------------------------------------------

/**
 * R7 #11 â€” Reset audio state on New Game / Return to Main Menu.
 *
 * After game-over â†’ New Game, the Zustand store is reset but audio.ts module
 * state (_currentBgm, _pendingBgmKey) persists across the React lifecycle.
 * The stale Howl keeps playing or the wrong key plays on next crossfade.
 *
 * Call this from resetGame() in game-store.ts before the store state update.
 * The caller is responsible for starting the appropriate BGM afterwards
 * (e.g. menu BGM when returning to MainMenu, or era-1-a when starting a new
 * game â€” done by startGame() which fires the intro cutscene + BGM).
 */
export function resetAudioForNewGame(): void {
  stopBGM(400);
  _pendingBgmKey = null;
  // R12 #3: clear seek cache â€” new game should start all tracks from 0:00.
  _bgmSeekCache.clear();
  // _audioContextUnlocked intentionally NOT reset â€” the browser already
  // unlocked the AudioContext; re-gating would silence the next BGM.
  console.info("[audio] resetAudioForNewGame: BGM stopped, pending + seek cache cleared.");
}

// ---------------------------------------------------------------------------
// Test helpers
// ---------------------------------------------------------------------------

/**
 * Test-only: clear internal caches + load-error memo. Call from afterEach in
 * tests that exercise audio paths to avoid Howl-instance bleed across tests.
 * R5 #8: also reset the unlock flag so tests can exercise the pre-unlock path.
 */
export function _resetAudioForTesting(): void {
  _sfxCache.clear();
  _loadErrorsLogged.clear();
  _currentBgm = null;
  _audioContextUnlocked = false;
  _pendingBgmKey = null;
  _pendingBgmDurationMs = 800;
  // R12 #3: clear seek cache between tests.
  _bgmSeekCache.clear();
}

/** Test-only: return the key of the currently playing BGM, or null. */
export function _currentBgmKey(): BGMKey | null {
  return _currentBgm?.key ?? null;
}

/**
 * Test-only: return the cached seek position (seconds) for a given key,
 * or undefined when no snapshot exists.
 * R12 #3 â€” used by seek-cache regression tests.
 */
export function _bgmSeekCacheGet(key: BGMKey): number | undefined {
  return _bgmSeekCache.get(key);
}

/**
 * Test-only: directly set a seek cache entry (simulate a prior playback).
 * R12 #3 â€” used by seek-cache regression tests.
 */
export function _bgmSeekCacheSet(key: BGMKey, sec: number): void {
  _bgmSeekCache.set(key, sec);
}
